/**
 * Cupones (docs/activa/FASE-4-PLAN.md, «Cupones»). Del equipo, bajo «Tu marca». Los cupones que la marca tiene
 * cargados en su sistema de pedidos, con el mismo código, descuento, compra mínima y vigencia: las campañas usan estos.
 * «Generar» propone un código que no se adivina. Un cupón no se borra: se archiva, así su código no se repite nunca.
 *
 * Todo en la página, sin ventanas emergentes (Alan, 23/09): el formulario se abre arriba de la lista o en lugar del
 * cupón que se edita.
 */

import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Loader2, Plus, Sparkles } from "lucide-react";
import { usePedido } from "../../emails/hooks/usePedido";
import { llamar } from "../../wa-oficial/components/base/comun";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { textoError } from "../../wa-oficial/logic/respuesta";
import { Opciones } from "../../segmentaciones/components/Opciones";
import { btnPrimario, btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { EncabezadoPagina, Pagina } from "../../../components/activa/Pagina";
import { R_DESCUENTOS, diaDe, hoyLocal } from "../../descuentos/logic/descuentos";
import { rutaMensajes } from "../../mensajes/logic/mensajes";
import { cuponesApi } from "../api";
import {
  DIAS_MAX, NO_INCLUYE_MAX, TEXTO_AVISO, avisosEnVivo, estadoDe, formularioDe, formularioVacio, ordenarCupones, problemaAlEscribir, textoOferta, textoVigencia,
  validarFormulario, type Campo, type Formulario,
} from "../logic/cupones";
import type { Cupon, ListaCupones } from "../types";

const campoClase = (conError: boolean) =>
  `min-h-[44px] w-full rounded-xl border bg-white px-3 text-sm text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${conError ? "border-red-400" : "border-slate-200"}`;
const tarjeta = "rounded-2xl border border-slate-200 bg-white p-5 space-y-5";
const ayuda = "text-sm text-slate-600";
const TONOS = { verde: "bg-emerald-50 text-emerald-700", rojo: "bg-red-50 text-red-700", ambar: "bg-amber-50 text-amber-800", gris: "bg-slate-100 text-slate-600" };
const CAMPOS: Campo[] = ["codigo", "descuentoPct", "compraMinima", "vigencia", "noIncluye"];

function ErrorDeCampo({ id, texto }: { id: string; texto?: string }) {
  if (!texto) return null;
  return (
    <p id={id} role="alert" className="text-sm font-medium text-red-700">
      {texto}
    </p>
  );
}

/** Cargar uno nuevo o editar uno guardado (sin el código: no se cambia). */
function FormularioCupon({ inicial, tope, onGuardado, onCancelar }: { inicial: Cupon | null; tope: number; onGuardado: (c: Cupon) => void; onCancelar: () => void }) {
  const hoy = hoyLocal();
  const editando = !!inicial;
  const [f, setF] = useState<Formulario>(() => (inicial ? formularioDe(inicial, hoy) : formularioVacio(hoy)));
  const [errores, setErrores] = useState<Partial<Record<Campo, string>>>({});
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [generando, setGenerando] = useState(false);
  /** Lo último que escribió la persona en el código: «Generar» parte de eso, no de un código que ya generó. */
  const [palabra, setPalabra] = useState("");
  const cambiar = (c: Partial<Formulario>) => setF((x) => ({ ...x, ...c }));
  const avisos = avisosEnVivo(f, tope, hoy);
  const prefijo = inicial ? `cupon-${inicial.id}` : "cupon-nuevo";

  // el espacio o un carácter que no va se avisan mientras se escribe; lo demás, al guardar
  const errorCodigo = errores.codigo || (editando ? null : problemaAlEscribir(f.codigo));

  const generar = async () => {
    setGenerando(true);
    setError("");
    // otra vez «Generar» sobre un código generado: de nuevo desde la palabra (si no, le sumaba 4 caracteres más)
    const l = await llamar<{ codigo: string }>(() => cuponesApi.generar(f.generado ? palabra : f.codigo));
    setGenerando(false);
    if (l.ok) {
      cambiar({ codigo: l.data.codigo, generado: true });
      setErrores((e) => ({ ...e, codigo: undefined }));
    } else setErrores((e) => ({ ...e, codigo: textoError(l) }));
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    const v = validarFormulario(f, editando);
    if (!v.ok) return setErrores(v.errores);
    setErrores({});
    setGuardando(true);
    const l = await llamar<Cupon>(() => (inicial ? cuponesApi.editar(inicial.id, v.cupon) : cuponesApi.crear(v.cupon)));
    setGuardando(false);
    if (l.ok) return onGuardado(l.data);
    const campo = typeof l.extra.campo === "string" ? (l.extra.campo.split(".")[0] as Campo) : null;
    if (campo && CAMPOS.includes(campo)) setErrores({ [campo]: textoError(l) });
    else setError(textoError(l));
  };

  return (
    <form onSubmit={guardar} noValidate className={`${tarjeta} border-violet-200`} aria-label={editando ? `Editar ${inicial?.codigo}` : "Nuevo cupón"} data-testid="formulario-cupon">
      <div className="space-y-1.5">
        {editando ? (
          <>
            <p className="text-sm font-semibold text-slate-900">Código</p>
            <p className="font-mono text-lg font-bold tracking-wide text-slate-900">{inicial?.codigo}</p>
            <p className={ayuda}>El código no se cambia: es el que queda en las campañas. Para usar otro, archivá este y cargá uno nuevo.</p>
          </>
        ) : (
          <>
            <label htmlFor={`${prefijo}-codigo`} className="block text-sm font-semibold text-slate-900">
              Código
            </label>
            <div className="flex flex-wrap gap-2">
              <input
                id={`${prefijo}-codigo`}
                value={f.codigo}
                maxLength={20}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => {
                  cambiar({ codigo: e.target.value.toUpperCase(), generado: false });
                  setPalabra(e.target.value.toUpperCase());
                }}
                placeholder="Ej: VOLVE7K2Q"
                aria-invalid={!!errorCodigo || undefined}
                aria-describedby={errorCodigo ? `${prefijo}-codigo-ayuda ${prefijo}-codigo-error` : `${prefijo}-codigo-ayuda`}
                className={`${campoClase(!!errorCodigo)} max-w-xs font-mono uppercase`}
              />
              <button type="button" className={btnSecundario} onClick={generar} disabled={generando}>
                {generando ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Sparkles size={16} aria-hidden="true" />}
                Generar
              </button>
            </div>
            <p id={`${prefijo}-codigo-ayuda`} className={ayuda}>
              Como está cargado en tu sistema de pedidos. O escribí una palabra y tocá «Generar»: le sumamos 4 caracteres al azar.
            </p>
            <ErrorDeCampo id={`${prefijo}-codigo-error`} texto={errorCodigo || undefined} />
          </>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor={`${prefijo}-pct`} className="block text-sm font-semibold text-slate-900">
          Descuento
        </label>
        <div className="flex max-w-[160px] items-center gap-2">
          <input
            id={`${prefijo}-pct`}
            inputMode="numeric"
            value={f.descuentoPct}
            onChange={(e) => cambiar({ descuentoPct: e.target.value })}
            placeholder="Ej: 15"
            aria-invalid={!!errores.descuentoPct || undefined}
            aria-describedby={errores.descuentoPct ? `${prefijo}-pct-error` : undefined}
            className={campoClase(!!errores.descuentoPct)}
          />
          <span className="text-sm font-semibold text-slate-600">%</span>
        </div>
        <ErrorDeCampo id={`${prefijo}-pct-error`} texto={errores.descuentoPct} />
        <Opciones
          nombre={`${prefijo}-sobre`}
          leyenda="Sobre"
          opciones={["pedido", "envio"] as const}
          valor={f.aplicaA}
          onCambio={(o) => cambiar({ aplicaA: o })}
          etiqueta={(o) => (o === "pedido" ? "El pedido" : "El envío")}
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor={`${prefijo}-minima`} className="block text-sm font-semibold text-slate-900">
          Compra mínima
        </label>
        <div className="flex max-w-[220px] items-center gap-2">
          <span className="text-sm font-semibold text-slate-600">$</span>
          <input
            id={`${prefijo}-minima`}
            inputMode="numeric"
            value={f.compraMinima}
            onChange={(e) => cambiar({ compraMinima: e.target.value })}
            placeholder="Sin mínimo"
            aria-invalid={!!errores.compraMinima || undefined}
            aria-describedby={errores.compraMinima ? `${prefijo}-minima-error` : undefined}
            className={campoClase(!!errores.compraMinima)}
          />
        </div>
        <ErrorDeCampo id={`${prefijo}-minima-error`} texto={errores.compraMinima} />
      </div>

      <div className="space-y-2">
        <Opciones
          nombre={`${prefijo}-vigencia`}
          leyenda="Cuándo vale"
          opciones={["dias", "fechas"] as const}
          valor={f.tipoVigencia}
          onCambio={(o) => cambiar({ tipoVigencia: o })}
          etiqueta={(o) => (o === "dias" ? "Días desde que le llega" : "Entre dos fechas")}
          invalida={!!errores.vigencia}
        />
        {f.tipoVigencia === "dias" ? (
          <label className="flex max-w-[220px] items-center gap-2 text-sm">
            <input
              inputMode="numeric"
              aria-label="Días desde que le llega"
              value={f.dias}
              onChange={(e) => cambiar({ dias: e.target.value })}
              aria-invalid={!!errores.vigencia || undefined}
              className={campoClase(!!errores.vigencia)}
            />
            <span className="whitespace-nowrap font-semibold text-slate-600">días (hasta {DIAS_MAX})</span>
          </label>
        ) : (
          <div className="grid max-w-md gap-3 sm:grid-cols-2">
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-slate-900">Desde el</span>
              <input type="date" value={f.desde} onChange={(e) => cambiar({ desde: e.target.value })} className={campoClase(!!errores.vigencia)} />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-slate-900">Hasta el</span>
              <input type="date" min={f.desde || undefined} value={f.hasta} onChange={(e) => cambiar({ hasta: e.target.value })} className={campoClase(!!errores.vigencia)} />
            </label>
          </div>
        )}
        <ErrorDeCampo id={`${prefijo}-vigencia-error`} texto={errores.vigencia} />
      </div>

      <div className="space-y-1.5">
        <label htmlFor={`${prefijo}-no-incluye`} className="block text-sm font-semibold text-slate-900">
          Qué no incluye
        </label>
        <input
          id={`${prefijo}-no-incluye`}
          value={f.noIncluye}
          maxLength={NO_INCLUYE_MAX}
          onChange={(e) => cambiar({ noIncluye: e.target.value })}
          placeholder="Ej: no aplica a las promos del día ni a los extras"
          aria-invalid={!!errores.noIncluye || undefined}
          className={campoClase(!!errores.noIncluye)}
        />
        <p className={ayuda}>El mensaje lo aclara. Dejalo vacío si vale para todo.</p>
        <ErrorDeCampo id={`${prefijo}-no-incluye-error`} texto={errores.noIncluye} />
      </div>

      {avisos.length > 0 && (
        <ul className="space-y-1 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="avisos-formulario">
          {avisos.map((a) => (
            <li key={a}>{TEXTO_AVISO[a]}</li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <button type="submit" className={btnPrimario} disabled={guardando}>
          {guardando && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          Guardar
        </button>
        <button type="button" className={btnSecundario} onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

/** Una acción de la fila: liviana (sin borde), para que la lista no sea una pila de botones; 44 px de alto igual. */
const btnFila =
  "min-h-[44px] px-3 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1.5 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400";

/** «Copiar»: lo primero que se hace con un cupón es cargarlo en el sistema de pedidos. */
function CopiarCodigo({ codigo }: { codigo: string }) {
  const [copiado, setCopiado] = useState(false);
  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 2000);
    return () => clearTimeout(t);
  }, [copiado]);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(codigo);
      setCopiado(true);
    } catch {
      /* sin portapapeles: el código queda a la vista para seleccionarlo */
    }
  };
  return (
    <button type="button" className={btnFila} onClick={() => void copiar()} aria-label={copiado ? `${codigo} copiado` : `Copiar ${codigo}`}>
      {copiado ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
      {copiado ? "Copiado" : "Copiar"}
    </button>
  );
}

function FilaCupon({ c, onEditar, onCambioEstado }: { c: Cupon; onEditar: () => void; onCambioEstado: (c: Cupon) => void }) {
  const [moviendo, setMoviendo] = useState(false);
  const [error, setError] = useState("");
  const estado = estadoDe(c);
  const mover = async () => {
    setMoviendo(true);
    setError("");
    const l = await llamar<Cupon>(() => (c.estado === "activo" ? cuponesApi.archivar(c.id) : cuponesApi.reactivar(c.id)));
    setMoviendo(false);
    if (l.ok) onCambioEstado(l.data);
    else setError(textoError(l));
  };
  return (
    <li className="flex flex-col gap-2 px-4 py-3.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4" data-testid={`cupon-${c.codigo}`}>
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-mono text-base font-bold tracking-wide text-slate-900 select-all">{c.codigo}</p>
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TONOS[estado.tono]}`}>{estado.texto}</span>
        </div>
        <p className="text-sm text-slate-800">
          {textoOferta(c)} · <span className="text-slate-600">{textoVigencia(c.vigencia)}</span>
        </p>
        {c.generado && <p className="text-[13px] text-slate-500">Generado por Activa: cargalo en tu sistema de pedidos con este código.</p>}
        {c.noIncluye && <p className="text-sm text-slate-600">No incluye: {c.noIncluye}</p>}
        {c.usadoEn && (
          <p className="text-sm text-slate-600" data-testid={`cupon-${c.codigo}-en-uso`}>
            Lo lleva {c.usadoEn.grupo ? <b className="font-semibold text-slate-800">{c.usadoEn.grupo}</b> : "un grupo"} de{" "}
            {c.usadoEn.campana ? <b className="font-semibold text-slate-800">{c.usadoEn.campana}</b> : "una campaña"}: no se puede usar en otro.{" "}
            <Link to={rutaMensajes(c.usadoEn.segmentacionId)} className="whitespace-nowrap font-semibold text-violet-700 underline">
              Ir a Mensajes
            </Link>
          </p>
        )}
        {c.avisos.filter((a) => a !== "vencido").length > 0 && (
          <ul className="space-y-1 text-sm text-amber-800">
            {c.avisos
              .filter((a) => a !== "vencido")
              .map((a) => (
                <li key={a}>{TEXTO_AVISO[a]}</li>
              ))}
          </ul>
        )}
        {c.estado === "archivado" && c.archivado && (
          <p className="text-sm text-slate-500">
            Archivado {c.archivado.autor?.nombre ? `por ${c.archivado.autor.nombre} ` : ""}el {diaDe(c.archivado.at)}. Su código sigue ocupado: no se repite.
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm font-medium text-red-700">
            {error}
          </p>
        )}
      </div>
      <div className="flex flex-none flex-wrap gap-1">
        <CopiarCodigo codigo={c.codigo} />
        <button type="button" className={btnFila} onClick={onEditar} aria-label={`Editar ${c.codigo}`}>
          Editar
        </button>
        <button type="button" className={btnFila} onClick={mover} disabled={moviendo} aria-label={`${c.estado === "activo" ? "Archivar" : "Reactivar"} ${c.codigo}`}>
          {moviendo && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          {c.estado === "activo" ? "Archivar" : "Reactivar"}
        </button>
      </div>
    </li>
  );
}

function Catalogo({ inicial }: { inicial: ListaCupones }) {
  const [lista, setLista] = useState(inicial);
  const [vista, setVista] = useState<"activos" | "archivados">("activos");
  const [nuevo, setNuevo] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [aviso, setAviso] = useState("");

  const ordenados = ordenarCupones(lista.cupones);
  const activos = ordenados.filter((c) => c.estado === "activo");
  const archivados = ordenados.filter((c) => c.estado === "archivado");
  const mostrados = vista === "activos" ? activos : archivados;

  /** Pone el cupón que volvió del servidor en la lista (uno nuevo, arriba de todo); el lugar lo decide `ordenarCupones`. */
  const reemplazar = (c: Cupon) =>
    setLista((l) => ({ ...l, cupones: l.cupones.some((x) => x.id === c.id) ? l.cupones.map((x) => (x.id === c.id ? c : x)) : [c, ...l.cupones] }));

  return (
    <div className="space-y-6">
      {!lista.descuentosGuardado && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="sin-descuentos">
          Todavía no guardaste <Link to={R_DESCUENTOS} className="font-semibold underline">Descuentos</Link>: hasta que lo hagas, ninguna campaña ofrece un cupón con descuento
          sobre el pedido.
        </p>
      )}

      {nuevo ? (
        <FormularioCupon
          inicial={null}
          tope={lista.tope}
          onGuardado={(c) => {
            reemplazar(c);
            setNuevo(false);
            setVista("activos");
            setAviso(`Cupón ${c.codigo} guardado.`);
          }}
          onCancelar={() => setNuevo(false)}
        />
      ) : (
        <button
          type="button"
          className={btnPrimario}
          onClick={() => {
            setNuevo(true);
            setEditando(null);
            setAviso("");
          }}
        >
          <Plus size={16} aria-hidden="true" />
          Nuevo cupón
        </button>
      )}
      {aviso && (
        <p role="status" className="text-sm text-emerald-700">
          {aviso}
        </p>
      )}

      <section className="space-y-3" aria-labelledby="lista-titulo">
        <h2 id="lista-titulo" className="sr-only">
          Tus cupones
        </h2>
        <Opciones
          nombre="vista-cupones"
          leyenda="Mostrar"
          opciones={["activos", "archivados"] as const}
          valor={vista}
          onCambio={setVista}
          etiqueta={(o) => (o === "activos" ? `Activos (${activos.length})` : `Archivados (${archivados.length})`)}
        />
        {mostrados.length === 0 ? (
          <p className={ayuda}>{vista === "activos" ? "Todavía no cargaste ningún cupón." : "No hay cupones archivados."}</p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="cupones">
            {mostrados.map((c) =>
              editando === c.id ? (
                <li key={c.id} className="p-2">
                  <FormularioCupon
                    inicial={c}
                    tope={lista.tope}
                    onGuardado={(x) => {
                      reemplazar(x);
                      setEditando(null);
                      setAviso(`Cupón ${x.codigo} guardado.`);
                    }}
                    onCancelar={() => setEditando(null)}
                  />
                </li>
              ) : (
                <FilaCupon
                  key={c.id}
                  c={c}
                  onEditar={() => {
                    setEditando(c.id);
                    setNuevo(false);
                    setAviso("");
                  }}
                  onCambioEstado={(x) => {
                    reemplazar(x);
                    setAviso(x.estado === "archivado" ? `Cupón ${x.codigo} archivado.` : `Cupón ${x.codigo} reactivado.`);
                  }}
                />
              ),
            )}
          </ul>
        )}
      </section>
    </div>
  );
}

export default function CuponesPage() {
  const pedido = usePedido<ListaCupones>(() => cuponesApi.listar(), "cupones");
  const d = pedido.dato;
  return (
    <Pagina angosta>
      <EncabezadoPagina antetitulo="Tu marca" titulo="Cupones" bajada="Los códigos de descuento de tus campañas. Cada uno tiene que estar cargado en tu sistema de pedidos, con el mismo descuento y la misma vigencia." />
      {pedido.error && !d && <ErrorTarjeta texto={pedido.error} onReintentar={pedido.recargar} />}
      {pedido.cargando && !d && <Esqueleto alto="h-64" etiqueta="Cargando los cupones" />}
      {d && <Catalogo inicial={d} />}
    </Pagina>
  );
}
